import React, { useRef, useState, useEffect } from 'react';
import { useReducedMotion, useInView } from 'framer-motion';
import { motion } from '../../lib/motionShim';
import { Volume2, VolumeX, Camera, Apple, Play, Star } from 'lucide-react';
import PhoneFrame from '../PhoneFrame';

/**
 * The receipts: a community reel playing inside a device at center,
 * flanked by two real transformations rendered as THEN/NOW cards.
 * Same motion language as every trio — center rises, wings fan out.
 */

const ReelPhone = ({ width }) => {
  const vidRef = useRef(null);
  const wrapRef = useRef(null);
  const [muted, setMuted] = useState(true);
  // Nothing downloads (poster or video) until the section approaches the viewport
  const [activated, setActivated] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return undefined;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setActivated(true);
          io.disconnect();
        }
      },
      { rootMargin: '400px' }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Play only while on screen — saves battery and bandwidth
  useEffect(() => {
    if (!activated) return undefined;
    const v = vidRef.current;
    if (!v) return undefined;
    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) v.play().catch(() => {});
        else v.pause();
      },
      { threshold: 0.35 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, [activated]);

  return (
    <div className="relative" ref={wrapRef}>
      <PhoneFrame width={width} glowColor="rgba(147,51,234,0.32)">
        {activated ? (
          <video
            ref={vidRef}
            src="/reel/ston-reel.mp4"
            poster="/reel/poster.webp"
            muted={muted}
            loop
            playsInline
            preload="metadata"
            className="w-full h-full object-contain bg-black"
          />
        ) : (
          <div className="w-full h-full bg-black" />
        )}
      </PhoneFrame>
      <button
        type="button"
        onClick={() => setMuted((m) => !m)}
        aria-label={muted ? 'Unmute reel' : 'Mute reel'}
        className="absolute bottom-[5%] right-[7%] z-10 flex h-10 w-10 items-center justify-center rounded-full border border-white/[0.15] bg-black/50 text-white backdrop-blur-md transition-colors hover:bg-ston-purple/60"
      >
        {muted ? <VolumeX size={17} /> : <Volume2 size={17} />}
      </button>
    </div>
  );
};

const TransformationCard = ({ then, now, stat, statNote, name, context, quote, fromX, delay }) => (
  <motion.div
    className="w-[280px] md:w-[300px] rounded-3xl border border-ston-border bg-white/[0.03] p-5"
    style={{ boxShadow: '0 30px 70px -25px rgba(0,0,0,0.8)' }}
    initial={{ opacity: 0, x: fromX, scale: 0.94 }}
    whileInView={{ opacity: 1, x: 0, scale: 1 }}
    viewport={{ once: true, margin: '-80px' }}
    transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
  >
    <div className="flex gap-3">
      {[
        { src: then, label: 'Then' },
        { src: now, label: 'Now' },
      ].map((p) => (
        <figure key={p.label} className="flex-1">
          <img
            src={p.src}
            alt={`${name} — ${p.label.toLowerCase()}`}
            loading="lazy"
            className="w-full aspect-[4/5] rounded-xl object-cover border border-white/[0.07]"
          />
          <figcaption className="mt-1.5 text-center font-mono text-[9.5px] uppercase tracking-[2px] text-ston-text-muted">
            {p.label}
          </figcaption>
        </figure>
      ))}
    </div>

    <div className="mt-4 flex items-baseline gap-2.5">
      <span
        className="font-outfit font-extrabold italic gradient-text leading-none tabular-nums"
        style={{ fontSize: '30px', letterSpacing: '-0.02em' }}
      >
        {stat}
      </span>
      <span className="font-mono text-[11px] tracking-wider text-ston-text-muted">{statNote}</span>
    </div>
    <p className="mt-2 text-[14.5px] font-semibold text-ston-text">
      {name} <span className="font-normal text-ston-text-muted">· {context}</span>
    </p>
    <p className="mt-2 text-[13px] leading-relaxed text-ston-text-secondary italic">"{quote}"</p>
  </motion.div>
);

/* Real words from the stores — verbatim, typos and all. That's the point. */
const storeReviews = [
  {
    title: 'THE GOAT',
    text: 'This is the best fitness app ive used hands down keep going great work!',
    name: 'GODLINESS123',
    store: 'App Store',
  },
  {
    text: 'Logging has made very simple in this app and the fact that i can find everything in one place is amazing. It is truly one app for fitness.',
    name: 'Rohit Ratnaparkhi',
    store: 'Google Play',
  },
  {
    text: 'It has made workouts fun for me — it gives me immense thrill and joy to daily complete my workouts, compare the numbers with my previous session and see where I have improved.',
    name: 'Tushar Jain',
    store: 'Google Play',
  },
  {
    text: 'It keeps track of my workouts and diet. It also keeps track of my recovery.',
    name: 'Vakul',
    store: 'Google Play',
  },
  {
    text: 'Been using it for a while, definitely a game changer app.',
    name: 'Prathamesh Autade',
    store: 'Google Play',
  },
  {
    text: 'Amazing',
    name: 'Salman',
    store: 'Google Play',
  },
];

const AVATAR_COLORS = [
  'linear-gradient(135deg, #9333ea, #6366f1)',
  'linear-gradient(135deg, #0ea5e9, #6366f1)',
  'linear-gradient(135deg, #f59e0b, #ef4444)',
  'linear-gradient(135deg, #10b981, #0ea5e9)',
  'linear-gradient(135deg, #ec4899, #9333ea)',
  'linear-gradient(135deg, #f97316, #f59e0b)',
];

const ReviewCard = ({ r, index }) => (
  <figure
    className={`w-[300px] md:w-[340px] shrink-0 rounded-2xl border bg-white/[0.03] p-5 transition-all duration-300 hover:-translate-y-1 hover:bg-white/[0.05] ${
      r.title
        ? 'border-ston-purple/40 shadow-[0_0_30px_-12px_rgba(147,51,234,0.5)]'
        : 'border-ston-border hover:border-ston-purple/30'
    }`}
  >
    <figcaption className="flex items-center gap-3">
      <span
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-[13px] font-bold text-white"
        style={{ background: AVATAR_COLORS[index % AVATAR_COLORS.length] }}
        aria-hidden="true"
      >
        {r.name[0].toUpperCase()}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-[13.5px] font-semibold text-ston-text">{r.name}</span>
        <span className="mt-0.5 flex items-center gap-1.5 text-[11px] text-ston-text-muted">
          <span className="flex items-center gap-[2px]" aria-label="5 out of 5 stars">
            {Array.from({ length: 5 }, (_, i) => (
              <Star key={i} size={11} className="text-amber-400" fill="currentColor" strokeWidth={0} />
            ))}
          </span>
          <span aria-hidden="true">·</span>
          {r.store === 'App Store' ? (
            <Apple size={11} className="shrink-0" />
          ) : (
            <Play size={10} className="shrink-0" fill="currentColor" />
          )}
          <span>{r.store}</span>
        </span>
      </span>
      {r.title && (
        <span className="ml-auto shrink-0 rounded-full border border-ston-purple/40 bg-ston-purple/10 px-2.5 py-1 font-outfit font-bold italic text-[10.5px] tracking-wide text-ston-purple-light">
          {r.title}
        </span>
      )}
    </figcaption>
    <blockquote className="mt-3.5 text-[14px] leading-relaxed text-ston-text-secondary">
      "{r.text}"
    </blockquote>
  </figure>
);

const ReviewsMarquee = () => {
  const marqueeRef = useRef(null);
  // Scroll the strip only while it's on screen
  const isOnScreen = useInView(marqueeRef, { margin: '100px' });
  // The marquee is cheap CSS — keep it on phones; only honor a real OS setting
  const reduce =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduce) {
    return (
      <div className="flex flex-wrap justify-center gap-4">
        {storeReviews.map((r, i) => (
          <ReviewCard key={r.name} r={r} index={i} />
        ))}
      </div>
    );
  }
  return (
    <div
      ref={marqueeRef}
      className="relative overflow-hidden py-2"
      style={{
        maskImage: 'linear-gradient(90deg, transparent, black 8%, black 92%, transparent)',
        WebkitMaskImage: 'linear-gradient(90deg, transparent, black 8%, black 92%, transparent)',
      }}
    >
      <div
        className="flex w-max gap-5 hover:[animation-play-state:paused]"
        style={{
          animation: 'proof-marquee 55s linear infinite',
          animationPlayState: isOnScreen ? 'running' : 'paused',
        }}
      >
        {[...storeReviews, ...storeReviews].map((r, i) => (
          <ReviewCard key={`${r.name}-${i}`} r={r} index={i % storeReviews.length} />
        ))}
      </div>
    </div>
  );
};

const ProofSection = () => {
  const reduce = useReducedMotion();

  return (
    <section
      id="proof"
      className="relative py-24 md:py-32 bg-ston-bg overflow-hidden"
      data-testid="proof-section"
    >
      <div aria-hidden="true" className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full" style={{ background: 'radial-gradient(closest-side, rgba(147,51,234,0.08), rgba(147,51,234,0.040) 55%, transparent 78%)' }} />
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center max-w-[760px] mx-auto">
          <motion.span
            className="section-label inline-block"
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.7 }}
          >
            PROOF
          </motion.span>
          <motion.h2
            className="mt-4 font-outfit font-extrabold italic uppercase leading-[1.02]"
            style={{ fontSize: 'clamp(30px, 4.2vw, 54px)', letterSpacing: '-0.025em' }}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <span className="block text-ston-text">It works.</span>
            <span className="block gradient-text">Here's the proof.</span>
          </motion.h2>
        </div>

        {/* Reel at center, transformations fanned out beside it */}
        <div className="mt-14 md:mt-16 flex flex-col lg:flex-row items-center justify-center gap-10 lg:gap-8">
          <div className="order-2 lg:order-1">
            <TransformationCard
              then="/proof/mihir-then.webp"
              now="/proof/mihir-now.webp"
              stat="+17 kg"
              statNote="48 → 65 KG"
              name="Mihir"
              context="IIT Bombay"
              quote="STON.FIT helped me track my strength — and connected me with a coach who showed me the right direction."
              fromX={reduce ? 0 : 120}
              delay={0.4}
            />
          </div>

          <motion.div
            className="order-1 lg:order-2 relative z-10"
            initial={reduce ? { opacity: 0 } : { opacity: 0, y: 44 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.9, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
          >
            <ReelPhone width={272} />
          </motion.div>

          <div className="order-3">
            {/* The open slot — the next transformation is the visitor's */}
            <motion.div
              className="w-[280px] md:w-[300px] rounded-3xl border-2 border-dashed border-ston-purple/35 bg-ston-purple/[0.04] p-5 flex flex-col items-center justify-center text-center"
              initial={{ opacity: 0, x: reduce ? 0 : -120, scale: 0.94 }}
              whileInView={{ opacity: 1, x: 0, scale: 1 }}
              viewport={{ once: true, margin: '-80px' }}
              transition={{ duration: 0.9, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            >
              {/* The unfilled pair — same frames as the card beside it */}
              <div className="flex w-full gap-3">
                <figure className="flex-1">
                  <div className="w-full aspect-[4/5] rounded-xl border-2 border-dashed border-ston-purple/30 bg-white/[0.02] flex flex-col items-center justify-center gap-1.5">
                    <Camera size={18} className="text-ston-purple-light/70" />
                    <span className="text-[12px] font-medium text-ston-text-secondary">Today</span>
                    <span className="font-mono text-[9.5px] tracking-[1px] text-ston-text-muted">
                      {new Date().toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric',
                      })}
                    </span>
                  </div>
                  <figcaption className="mt-1.5 text-center font-mono text-[9.5px] uppercase tracking-[2px] text-ston-text-muted">
                    Then
                  </figcaption>
                </figure>
                <figure className="flex-1">
                  <div className="w-full aspect-[4/5] rounded-xl border-2 border-dashed border-white/[0.12] bg-white/[0.02] flex flex-col items-center justify-center gap-1.5">
                    <span className="font-outfit font-extrabold italic text-[26px] leading-none text-ston-text-muted">
                      ?
                    </span>
                    <span className="text-[12px] font-medium text-ston-text-secondary">Month 6</span>
                  </div>
                  <figcaption className="mt-1.5 text-center font-mono text-[9.5px] uppercase tracking-[2px] text-ston-text-muted">
                    Now
                  </figcaption>
                </figure>
              </div>

              <span
                className="mt-5 inline-block font-outfit font-extrabold italic gradient-text leading-none"
                style={{ fontSize: '44px', letterSpacing: '-0.02em', padding: '0.06em 0.18em' }}
              >
                You?
              </span>
              <p className="mt-3 text-[14px] leading-relaxed text-ston-text-secondary max-w-[24ch]">
                This spot is open. Day one gets tracked — month three gets framed.
              </p>
              <button
                type="button"
                onClick={() =>
                  document.getElementById('waitlist')?.scrollIntoView({ behavior: 'smooth' })
                }
                className="mt-6 inline-flex items-center gap-2 rounded-full border border-ston-purple/45 bg-ston-purple/10 px-5 py-2.5 text-[13.5px] font-semibold text-ston-text transition-colors hover:bg-ston-purple/25"
              >
                Start your line <span aria-hidden="true">→</span>
              </button>
            </motion.div>
          </div>
        </div>

        {/* Straight from the stores */}
        <motion.div
          className="mt-16 md:mt-20"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        >
          <div className="mb-8 text-center">
            <h3
              className="font-outfit font-extrabold italic uppercase leading-none"
              style={{ fontSize: 'clamp(22px, 2.6vw, 32px)', letterSpacing: '-0.02em' }}
            >
              <span className="text-ston-text">The reviews </span>
              <span className="gradient-text">are in.</span>
            </h3>
            <p className="mt-3 font-mono text-[11px] uppercase tracking-[2.5px] text-ston-text-muted">
              Rated <span className="text-ston-text-secondary">4.5<span className="text-amber-400">★</span></span> on
              the App Store — and climbing.
            </p>
          </div>
          <ReviewsMarquee />
        </motion.div>
      </div>
    </section>
  );
};

export default ProofSection;
